import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../Layouts/Navbar/Navbar";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import LeftSideBar, { FolderCollapse } from "../../components/SideBar/LeftSideBar";
import { useModalContext } from "../../context/ModalContext";
import { useNavigationContext } from "../../context/NavigationContext";
import { useTemplateContext } from "../../context/TemplateContext";
import { useTreeContext } from "../../context/TreeDataContext";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Button from "../../components/FormComponents/Button";
import ResizableComponent from "../../Layouts/ResizableComponent";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";
import CreateTemplate from "./CreateTemplate";
import ErrorMessage from "../../components/FormComponents/ErrorMessage";
import "./Template.css";

const TemplateSection = () => {
  const [isLoader, setIsLoader] = useState(false);
  const [createTemplateModalData, setCreateTemplateModalData] = useState(null);
  const { templateExplorerData, setTemplateExplorerData } = useTreeContext();
  const { selectedTemplate, setSelectedTemplate, fetchAllTemplates } = useTemplateContext();
  const { getUserConfirmationForDelete } = useModalContext();
  const { saveChangesAndNavigate } = useNavigationContext();
  const navigate = useNavigate();
  const [isError, setIsError] = useState(true);
  const { templateId } = useParams();

  const createTemplate = async (folder, config) => {
    try {
      const response = await axiosInstance.post(`/${folder.parentData}`, config);
      const { data, message } = response;

      const newItem = fileFolderConfig.getFileConfig(data.id, data.name, folder.parentData);
      const newTree = insertNewFileOrFolder(templateExplorerData, folder, newItem);
      setCreateTemplateModalData(null);
      setTemplateExplorerData(newTree);
      navigate(`./${data.id}`);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleNewInsert = async (folder, name) => {
    if (folder.insert_type === "popup") {
      setCreateTemplateModalData(folder);
      return;
    }

    if (name.trim().length < 3) {
      toastError("Name must be at least 3 characters");
      return;
    }

    const config = { name };
    await createTemplate(folder, config);
  };

  const handleEdit = async (folder, newName) => {
    if (newName.trim().length < 3) {
      toastError("Name must be at least 3 characters");
      return;
    }

    try {
      const response = await axiosInstance.patch(`/${folder.parentData}/${folder.id}`, { name: newName });
      const { message } = response;

      const newTree = editFolderOrFile(templateExplorerData, folder, newName);
      setTemplateExplorerData(newTree);

      if (folder.id === selectedTemplate?.id) {
        setSelectedTemplate({ ...selectedTemplate, name: newName });
      }

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDelete = async (folder) => {
    const userConfirmed = await getUserConfirmationForDelete(folder.name);
    if (!userConfirmed) return;

    try {
      const response = await axiosInstance.delete(`/${folder.parentData}/${folder.id}`);
      const { message } = response;

      const newTree = deleteFolderOfFile(templateExplorerData, folder);
      setTemplateExplorerData(newTree);

      if (folder.id === selectedTemplate?.id) {
        navigate("./");
      }

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const callbackFunction = (id) => {
    if (selectedTemplate.id === id) return;
    saveChangesAndNavigate(id);
  };

  const initialFetching = async () => {
    setIsLoader(true);
    setIsError(false);
    try {
      await fetchAllTemplates();
      if (selectedTemplate.id && !templateId) {
        setSelectedTemplate({ id: "" });
      }
      setIsLoader(false);
    } catch (error) {
      setIsError(true);
      setIsLoader(false);
    }
  };

  useEffect(() => {
    initialFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section className="main_section">
        <div className="sub_section">
          <ResizableComponent
            resizeSide="right"
            storageKey="left_sidebar_size"
            defaultSize={244}
            range={[244, 400]}
            collapsible={true}
          >
            <HeaderModes />
            <div className="d_flex">
              <Navbar />
              {isError ? (
                <div className="d_flex align_center justify_center flex_column">
                  <ErrorMessage className={"text_center mb_3"}>Failed to load templates. Please try again</ErrorMessage>
                  <Button onClick={initialFetching}>Retry</Button>
                </div>
              ) : (
                <LeftSideBar
                  explorerData={templateExplorerData}
                  setExplorerData={setTemplateExplorerData}
                  selectedScreen={selectedTemplate}
                  callbackFunction={callbackFunction}
                  handleNewInsert={handleNewInsert}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  type="Templates"
                  isLoader={isLoader}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <FolderCollapse
                      explorerData={templateExplorerData}
                      setExplorerData={setTemplateExplorerData}
                      type="template"
                    />
                  </div>
                </LeftSideBar>
              )}
            </div>
          </ResizableComponent>

          {!isLoader && !isError && <Outlet />}
        </div>

        <BuilderModal
          isOpen={createTemplateModalData}
          onClose={() => setCreateTemplateModalData(null)}
          modalName="Create Masterdata"
        >
          <CreateTemplate create={(data) => createTemplate(createTemplateModalData, data)} />
        </BuilderModal>
      </section>
    </>
  );
};

export default TemplateSection;
